import React, { useState, useEffect } from 'react';
import { FileText, Calendar, User, Search, Download, Printer, Eye } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '../../utils/dateTimeUtils';

const CertificateHistory = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/certificates/history');
      setCertificates(response.data || []);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = 
      cert.heading?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certificate_no?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesDate = true;
    if (dateFilter.from && dateFilter.to) {
      const certDate = new Date(cert.date);
      const fromDate = new Date(dateFilter.from);
      const toDate = new Date(dateFilter.to);
      matchesDate = certDate >= fromDate && certDate <= toDate;
    }
    
    return matchesSearch && matchesDate;
  });

  const handleViewCertificate = (cert) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${cert.heading}</title>
        <style>
          @page { size: A4; margin: 25mm; }
          body { font-family: 'Times New Roman', serif; line-height: 1.6; }
          .certificate-container { max-width: 210mm; margin: 0 auto; padding: 20px; }
          .certificate-header { text-align: center; margin: 30px 0; }
          .certificate-heading { font-size: 22px; font-weight: bold; text-decoration: underline; }
          .certificate-meta { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 20px; }
          .certificate-body { text-align: justify; font-size: 14px; margin: 30px 0; }
          .certificate-body table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          .certificate-body td, .certificate-body th { border: 1px solid #333; padding: 8px; }
        </style>
      </head>
      <body>
        <div class="certificate-container">
          <div class="certificate-header">
            <div class="certificate-heading">${cert.heading}</div>
          </div>
          <div class="certificate-meta">
            <div><strong>Certificate No:</strong> ${cert.certificate_no}</div>
            <div><strong>Date:</strong> ${formatDate(cert.date)}</div>
          </div>
          <div class="certificate-body">${cert.body_html}</div>
        </div>
        <button onclick="window.print()" style="position: fixed; top: 10px; right: 10px; padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer;">Print</button>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const exportToCSV = () => {
    const headers = ['Certificate No', 'Heading', 'Date', 'Created By', 'Created At'];
    const rows = filteredCertificates.map(cert => [
      cert.certificate_no,
      cert.heading,
      formatDate(cert.date),
      cert.created_by,
      new Date(cert.created_at).toLocaleString('en-GB')
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `certificate_history_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="text-blue-600" />
            Certificate History Report
          </h1>
          <p className="text-gray-600 mt-1">View all manually printed certificates</p>
        </div>
        <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Certificates</p>
              <p className="text-3xl font-bold text-gray-800">{certificates.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FileText className="text-blue-600 h-6 w-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">This Month</p>
              <p className="text-3xl font-bold text-gray-800">
                {certificates.filter(c => {
                  const certDate = new Date(c.created_at);
                  const now = new Date();
                  return certDate.getMonth() === now.getMonth() && certDate.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Calendar className="text-green-600 h-6 w-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Filtered Results</p>
              <p className="text-3xl font-bold text-gray-800">{filteredCertificates.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Search className="text-purple-600 h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search by heading or certificate number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <Input
              type="date"
              value={dateFilter.from}
              onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <Input
              type="date"
              value={dateFilter.to}
              onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : filteredCertificates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p>No certificates found</p>
            <p className="text-sm">Print certificates from "Manual Certificate Print" to see them here</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certificate No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Heading
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCertificates.map((cert, index) => (
                <tr key={cert.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {cert.certificate_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {cert.heading}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cert.date ? formatDate(cert.date) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cert.created_by || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cert.created_at ? new Date(cert.created_at).toLocaleString('en-GB') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleViewCertificate(cert)}
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      View/Print
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CertificateHistory;
